import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import { boolean } from '../form-boolean/lfds-boolean.css.ts'
import styles from '../form-boolean-card/lfds-boolean-card.css.ts'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import { WithFocus, compose } from '#utils/mixins'

/**
 * @name LfdsRadioCard
 * @tag lfds-radio-card
 * @status draft
 *
 * @description
 * Radio card variant. Always use inside `lfds-radio-group`, which owns
 * form participation, mutual exclusion, and keyboard navigation.
 */
export default class LfdsRadioCard extends compose(LfuiElement, WithFocus) {
	static styles = [LfuiElement.styles, styles, boolean]
	static events = ['change']

	protected delegatedEvents = ['change', 'focus', 'blur', 'focusin', 'focusout']

	@property() label!: string
	@property() name: string
	@property() value: string
	@property() description: string
	@property({ attribute: 'icon-id' }) iconId: string
	@property() layout: 'vertical' | 'horizontal' | 'auto' = 'auto'
	@property({ attribute: 'help-text' }) helpText: string
	@property() autocomplete: 'on' | 'off'
	@property({ type: Boolean }) indeterminate: boolean = false
	@property({ type: Boolean }) checked: boolean = false
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) disabled: boolean
	@property({ type: Boolean }) invalid: boolean
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	// Plain ref — no form internals needed
	#input = createRef<HTMLInputElement>()
	#labels = refCollection()
	#descriptions = refCollection()

	constructor() {
		super()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
		this.addEventListener('click', this.#handleHostClick)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.#handleHostClick)
	}

	#handleHostClick = (event: Event) => {
		if (event.composedPath()[0] === this) {
			this.#input.value?.click()
		}
	}

	#handleChange = (_event: Event) => {
		this.checked = true
		this.invalid = false
		// Dispatch an explicit change event so the parent lfds-radio-group
		// receives it in #onChildChange and handles mutual exclusion + form value.
		this.dispatchEvent(
			new CustomEvent('change', {
				detail: { value: this.value },
				bubbles: true,
				composed: true
			})
		)
	}

	render() {
		return html`
			<div
				class=${classMap({
					card: true,
					vertical: this.layout === 'vertical',
					horizontal: this.layout === 'horizontal',
					'focus-visible': this.focusVisible
				})}
			>
				<div class="control ${this.checked ? 'checked' : ''}">
					<input
						id="input"
						type="radio"
						class="boolean radio"
						aria-invalid=${this.invalid ? 'true' : (nothing as any)}
						aria-label=${ifDefined(this.srLabel)}
						value=${ifDefined(this.value)}
						name=${ifDefined(this.name)}
						?required=${this.required}
						?disabled=${this.disabled}
						.checked=${this.checked}
						${ref(this.#input)}
						${delegateEvents(this.delegatedEvents, {
							before: { change: this.#handleChange }
						})}
						${this.#labels.bind('ariaLabelledByElements')}
						${this.#descriptions.bind('ariaDescribedByElements')}
						${ariaElements({
							describedBy: this.srDescribedby,
							labelledBy: this.srLabelledby,
							controls: this.srControls
						})}
					/>
				</div>
				<label class="label" for="input" ${this.#labels.add()}>
					<slot name="label">${this.label}</slot>
				</label>
				<p class="description" ${this.#descriptions.add()}>
					<slot name="description">${this.description}</slot>
				</p>
				<span class="figure">
					<slot name="icon">
						${this.iconId
							? html`<lfds-icon icon-id=${this.iconId} size="40"></lfds-icon>`
							: nothing}
					</slot>
				</span>
			</div>
		`
	}
}
